import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  ListItem,
  Tag,
  Text,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import SpannerIcon from "assets/icons/medium/spanner.svg?react";
import { CardRecord } from "components/cards/card-record";
import { CardRecordInfoTable } from "components/cards/card-record-info-table";
import { CardRecordInsetDescription } from "components/cards/card-record-inset-description";
import { CardRecordMeta } from "components/cards/card-record-meta";
import {
  MaintenanceTaskStatus,
  type MaintenanceTask,
} from "types/maintenance-task";
import { dateFormats } from "utils/date";
import { getHighestResolutionImageSource } from "utils/images";
import { titleCase } from "utils/string";

const statusColorMap = {
  [MaintenanceTaskStatus.JobLogged]: "#e7e8e9",
  [MaintenanceTaskStatus.QuotesRequested]: "#ffec9e",
  [MaintenanceTaskStatus.QuotesIssued]: "#ffec9e",
  [MaintenanceTaskStatus.QuotesReceived]: "#ffec9e",
  [MaintenanceTaskStatus.QuotesApproved]: "#ffec9e",
  [MaintenanceTaskStatus.WorkOrderIssued]: "#ffec9e",
  [MaintenanceTaskStatus.WorkOrderAssigned]: "#ffec9e",
  [MaintenanceTaskStatus.WorkFinished]: "#c4e9d5",
};

export function MaintenanceTaskListItem({
  task,
  setCurrentOpenTaskId,
}: {
  task: MaintenanceTask;
  setCurrentOpenTaskId: (taskId: string) => void;
}) {
  const taskImageLength = task?.images?.length ?? 0;
  const displayImage = task?.images![0];

  return (
    <ListItem>
      <CardRecord
        heading={task.summary}
        leftElement={
          <Box
            borderRadius={"lg"}
            w={["90px", "160px"]}
            h={["68px", "120px"]}
            bg={"gray.100"}
            alignItems={"center"}
            justifyContent={"center"}
            overflow={"hidden"}
            flexShrink={0}
            alignSelf={"flex-start"}
            display={"flex"}
          >
            {displayImage ? (
              <Image
                src={getHighestResolutionImageSource(displayImage)?.url}
                alt={"Maintenance task image"}
                objectFit={"cover"}
                w={"100%"}
                h={"100%"}
              />
            ) : (
              <Icon as={SpannerIcon} w={10} h={10} color={"gray.300"} />
            )}
          </Box>
        }
      >
        <CardRecordMeta>
          {task.closed_at === null && (
            <Tag
              color={"black"}
              bg={statusColorMap[task.status_id]}
              fontSize={"sm"}
              fontWeight={600}
              lineHeight={"16px"}
              py={0.5}
              px={2}
              whiteSpace={"nowrap"}
            >
              {titleCase(task.status_id)}
            </Tag>
          )}
          <Text fontSize={"md"}>
            Last updated{" "}
            {dayjs(task.updated_at).format(dateFormats.dayMonthYear)}
          </Text>
        </CardRecordMeta>
        <CardRecordInsetDescription
          color={"gray.300"}
          showMoreElement={
            <Flex direction={"column"} gap={4}>
              {task.description ? (
                <Text textStyle={"large"} color={"gray.600"}>
                  {task.description}
                </Text>
              ) : null}
              <CardRecordInfoTable
                rows={[
                  {
                    label: "Requested on",
                    value: task.reported_date
                      ? dayjs(task.reported_date).format(
                          dateFormats.dayMonthYear,
                        )
                      : null,
                  },
                  {
                    label: "Access provided by",
                    value: task.access_provided_by,
                  },
                  {
                    label: "Images",
                    value: (
                      <Flex direction={"row"} gap={1}>
                        <Text fontSize={"lg"}>
                          {taskImageLength} image
                          {taskImageLength === 1 ? "" : "s"}
                        </Text>
                        {taskImageLength > 0 && (
                          <Button
                            variant={"link"}
                            fontSize={"lg"}
                            color={"blue.600"}
                            onClick={() => setCurrentOpenTaskId(task.id)}
                          >
                            View
                          </Button>
                        )}
                      </Flex>
                    ),
                  },
                ]}
              />
            </Flex>
          }
        />
      </CardRecord>
    </ListItem>
  );
}
